import { CheckIcon, ChevronDown } from "lucide-react";
import { type KeyboardEvent, forwardRef, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useFormField } from "uiShadcn/form";
import { Popover, PopoverContent, PopoverTrigger } from "uiShadcn/popover";
import capitalize from "utils/helpers/captizalize";
import { cn } from "utils/helpers/cn";

export interface MultiSelectProps {
    options: {
        key: string;
        value: string;
    }[];
    onValueChange: (value: string[]) => void;
    defaultValue: string[];
    dataTestId: string;

    placeholder?: string;
    maxCount?: number;
    className?: string;
    /**
     * The modality of the popover. When set to true, interaction with outside elements
     * will be disabled and only popover content will be visible to screen readers.
     * Optional, defaults to false.
     */
    modalPopover?: boolean;
    required?: boolean;
}

export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
    (
        {
            onValueChange,
            defaultValue = [],
            placeholder,
            maxCount = 3,
            modalPopover = false,
            className,
            dataTestId,
            required = false,
            ...rest
        },
        ref,
    ) => {
        const { formatMessage } = useIntl();
        const { error, formItemId } = useFormField();

        const [selectedValues, setSelectedValues] = useState<string[]>(defaultValue);
        const [isPopoverOpen, setIsPopoverOpen] = useState(false);

        const [focusedIndex, setFocusedIndex] = useState<number | null>(0);
        const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
        const triggerRef = useRef<HTMLButtonElement>(null);
        const parsedOptions = rest.options.map((option) => {
            return {
                ...option,
                key: option.key.toString(),
            };
        });

        const toggleOption = (value: string) => {
            const newSelectedValues = selectedValues.includes(value)
                ? selectedValues.filter((v) => v !== value)
                : [...selectedValues, value];

            setSelectedValues(newSelectedValues);
            onValueChange(newSelectedValues);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            const { key } = event;

            if (key === "ArrowDown" || key === "ArrowRight") {
                event.preventDefault();
                setFocusedIndex((prev) => (prev === null ? 0 : (prev + 1) % parsedOptions.length));
            } else if (key === "ArrowUp" || key === "ArrowLeft") {
                event.preventDefault();
                setFocusedIndex((prev) =>
                    prev === null ? parsedOptions.length - 1 : (prev - 1 + parsedOptions.length) % parsedOptions.length,
                );
            } else if (key === "Enter" || key === " ") {
                event.preventDefault();
                toggleOption(parsedOptions[focusedIndex ?? 0].key);
            }
        };

        useEffect(() => {
            const time = setTimeout(() => {
                if (isPopoverOpen) {
                    optionRefs.current[focusedIndex ?? 0]?.focus();
                }
            }, 0);
            return () => clearTimeout(time);
        }, [focusedIndex, isPopoverOpen]);

        const handleOpenChange = (open: boolean) => {
            setIsPopoverOpen(open);
            if (!open) {
                triggerRef.current?.focus();
            }
        };

        return (
            <Popover open={isPopoverOpen} onOpenChange={handleOpenChange} modal={modalPopover}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        ref={(el) => {
                            if (typeof ref === "function") {
                                ref(el);
                            } else if (ref) {
                                ref.current = el;
                            }
                            triggerRef.current = el;
                        }}
                        className={cn(
                            "flex items-center justify-between rounded-md border border-input bg-background px-n3 py-n4",
                            "text-font-2 text-sm ring-offset-background",
                            "focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
                            {
                                "border-destructive focus:ring-destructive": !!error,
                            },
                            className,
                        )}
                        data-test-id={dataTestId}
                        aria-required={required}
                        aria-invalid={required && selectedValues.length === 0}
                        id={formItemId}
                    >
                        {selectedValues.length > 0 ? (
                            <p className="flex items-center gap-2 dark:text-foreground">
                                {selectedValues?.length > maxCount
                                    ? formatMessage({ id: "selected" }, { count: selectedValues?.length })
                                    : selectedValues
                                          ?.map((e) => {
                                              const name = parsedOptions.find((o) => o.key === e)?.value;

                                              return name ? capitalize(name) : null;
                                          })
                                          ?.join(", ")}
                            </p>
                        ) : (
                            <span className="text-muted-foreground">{placeholder}</span>
                        )}

                        <ChevronDown className="h-5 w-5" />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    className="max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] overflow-auto bg-background p-0"
                    onEscapeKeyDown={() => setIsPopoverOpen(false)}
                    onKeyDown={handleKeyDown}
                >
                    <ul
                        className="flex flex-col gap-n1 p-n1"
                        // biome-ignore lint:
                        role="listbox"
                        aria-activedescendant={focusedIndex !== null ? `option-${parsedOptions[focusedIndex].key}` : undefined}
                        tabIndex={-1}
                    >
                        {parsedOptions.map((option, index) => {
                            const isSelected = selectedValues.includes(option.key);
                            return (
                                // biome-ignore lint:
                                <li
                                    key={option.key}
                                    onClick={() => toggleOption(option.key)}
                                    id={`option-${option.key}`}
                                    data-test-id={`${dataTestId}-option-${option.key}`}
                                    // biome-ignore lint:
                                    role="option"
                                    aria-selected={isSelected}
                                    className={cn(
                                        "group flex cursor-pointer items-center gap-n2 rounded-sm px-2 py-1.5 text-foreground text-sm",
                                        "hover:bg-primary-foreground hover:text-black",
                                        "focus:outline-hidden focus:ring-2 focus:ring-ring",
                                    )}
                                    ref={(el) => {
                                        optionRefs.current[index] = el;
                                    }}
                                    tabIndex={-1}
                                >
                                    <div
                                        className={cn(
                                            "flex h-4 w-4 items-center justify-center rounded-nsm border border-checkbox",
                                            "dark:group-hover:border-black dark:group-hover:bg-black",
                                            "dark:group-focus:border-black dark:group-focus:bg-black",
                                            isSelected
                                                ? "bg-primary text-primary-foreground"
                                                : "opacity-50 dark:group-focus:opacity-100 dark:group-hover:opacity-100 [&_svg]:invisible",
                                        )}
                                    >
                                        <CheckIcon className="h-4 w-4" />
                                    </div>
                                    <span>{capitalize(option.value)}</span>
                                </li>
                            );
                        })}
                    </ul>
                </PopoverContent>
            </Popover>
        );
    },
);

MultiSelect.displayName = "MultiSelect";
